import type { PriceTable } from "../pricing/pricing";
import type { TurnEvent } from "../types";

export const WINDOW_MS = 5 * 60 * 60 * 1000;

/**
 * Anthropic-style rolling 5h session window: a window opens at the first
 * activity after the previous window expired and lasts exactly 5 hours.
 * This is an estimate of quota accounting, not an official reconstruction.
 */
export interface QuotaWindow {
  start: number;
  end: number;
  turns: number;
  totalTokens: number;
  outputTokens: number;
  costUsd: number;
  unpricedTurns: number;
}

export function build5hWindows(turns: TurnEvent[], prices: PriceTable): QuotaWindow[] {
  const sorted = turns.filter((t) => t.timestamp > 0).sort((a, b) => a.timestamp - b.timestamp);
  const windows: QuotaWindow[] = [];
  let current: QuotaWindow | undefined;
  for (const t of sorted) {
    if (!current || t.timestamp >= current.end) {
      current = {
        start: t.timestamp,
        end: t.timestamp + WINDOW_MS,
        turns: 0,
        totalTokens: 0,
        outputTokens: 0,
        costUsd: 0,
        unpricedTurns: 0,
      };
      windows.push(current);
    }
    current.turns++;
    current.totalTokens += t.usage.input + t.usage.output + t.usage.cacheRead + t.usage.cacheWrite;
    current.outputTokens += t.usage.output;
    const cost = prices.turnCostUsd(t);
    if (cost === null) current.unpricedTurns++;
    else current.costUsd += cost;
  }
  return windows;
}

export interface WeekBucket {
  weekKey: string;
  start: number;
  turns: number;
  totalTokens: number;
  costUsd: number;
}

export function weeklyRollup(turns: TurnEvent[], prices: PriceTable): WeekBucket[] {
  const byWeek = new Map<string, WeekBucket>();
  for (const t of turns) {
    if (t.timestamp <= 0) continue;
    const weekKey = isoWeekKey(t.timestamp);
    let bucket = byWeek.get(weekKey);
    if (!bucket) {
      bucket = { weekKey, start: t.timestamp, turns: 0, totalTokens: 0, costUsd: 0 };
      byWeek.set(weekKey, bucket);
    }
    bucket.start = Math.min(bucket.start, t.timestamp);
    bucket.turns++;
    bucket.totalTokens += t.usage.input + t.usage.output + t.usage.cacheRead + t.usage.cacheWrite;
    const cost = prices.turnCostUsd(t);
    if (cost !== null) bucket.costUsd += cost;
  }
  return [...byWeek.values()].sort((a, b) => a.start - b.start);
}

/** ISO-8601 week key (local time), e.g. 2026-W28. */
export function isoWeekKey(ts: number): string {
  const d = new Date(ts);
  const thursday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const weekday = (thursday.getDay() + 6) % 7; // Monday = 0
  thursday.setDate(thursday.getDate() - weekday + 3);
  const year = thursday.getFullYear();
  const jan4 = new Date(year, 0, 4);
  const week1Monday = new Date(year, 0, 4 - ((jan4.getDay() + 6) % 7));
  const week = Math.round((thursday.getTime() - week1Monday.getTime()) / (7 * 86400000)) + 1;
  return `${year}-W${String(week).padStart(2, "0")}`;
}
